import { Box, Button, Stack, type SxProps, type Theme } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { AttachmentMessageContent } from './attachment-message-content';
import {
  attachmentRowSx,
  COMPACT_ATTACHMENT_MIN_HEIGHT,
  getAttachmentItemSx,
  isSupportedAudioAttachmentContentType,
} from './attachment-message-content.utils';

// When collapsed, the attachment row is limited to this many rows; pills beyond
// it are hidden behind the inline "+N more" control. We measure rather than cap
// by a fixed count because how many compact pills fit per row depends on width.
const MAX_COLLAPSED_ROWS = 2;

type AttachmentWithUrl = {
  id: string;
  contentType: string;
  fileName: string;
  size: number;
  url: string;
};

type MessageAttachmentFile = File | AttachmentWithUrl;

// Styled to match the compact attachment pills (fill, border, radius, height)
// and rendered inline as the last chip so it flows with them.
const attachmentMoreButtonSx: SxProps<Theme> = {
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'action.hover',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  color: 'text.secondary',
  flexShrink: 0,
  fontSize: (theme) => theme.typography.caption.fontSize,
  fontWeight: 600,
  lineHeight: 1.2,
  minHeight: (theme) => theme.spacing(COMPACT_ATTACHMENT_MIN_HEIGHT),
  minWidth: 0,
  px: 0.75,
  py: 0,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'action.selected',
    borderColor: 'divider',
  },
};

const isAttachmentWithUrl = (file: MessageAttachmentFile): file is AttachmentWithUrl =>
  Boolean(
    file &&
      typeof file === 'object' &&
      'id' in file &&
      'url' in file &&
      !(file instanceof File),
  );

export const MessageAttachments = ({
  attachments,
}: {
  attachments: MessageAttachmentFile[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const visibleAttachments = attachments.filter(isAttachmentWithUrl);
  const hasMultipleAttachments = visibleAttachments.length > 1;
  const attachmentLayoutSignature = visibleAttachments
    .map((attachment) => `${attachment.id}:${attachment.contentType}`)
    .join('|');

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || !hasMultipleAttachments) {
      // Clear any stale hiding (e.g. if the set shrank back to a single pill).
      if (grid) {
        Array.from(grid.children).forEach((child) => {
          if (child instanceof HTMLElement) child.style.removeProperty('display');
        });
      }
      setHiddenCount(0);
      return undefined;
    }

    const measure = () => {
      const toggle = toggleRef.current;
      const pills = Array.from(grid.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement && child !== toggle,
      );
      if (!pills.length) {
        setHiddenCount(0);
        return;
      }

      // Read the natural layout with every pill shown.
      pills.forEach((pill) => pill.style.removeProperty('display'));

      if (new Set(pills.map((pill) => pill.offsetTop)).size <= MAX_COLLAPSED_ROWS) {
        if (toggle) toggle.style.display = 'none';
        setHiddenCount(0);
        return;
      }

      // Overflow: keep the toggle in flow and hide pills from the end until it
      // sits within the allowed rows.
      if (toggle) toggle.style.removeProperty('display');

      const toggleWithinRows = () => {
        if (!toggle) return true;
        const rowTops = [
          ...new Set(
            pills
              .filter((pill) => pill.style.display !== 'none')
              .map((pill) => pill.offsetTop),
          ),
        ].sort((a, b) => a - b);
        const lastAllowedRowTop = rowTops[Math.min(MAX_COLLAPSED_ROWS, rowTops.length) - 1];
        return toggle.offsetTop <= lastAllowedRowTop;
      };

      let hidden = 0;
      while (hidden < pills.length - 1 && !toggleWithinRows()) {
        pills[pills.length - 1 - hidden].style.display = 'none';
        hidden += 1;
      }

      // Expanded: reveal everything; the toggle becomes "Show less".
      if (isExpanded) {
        pills.forEach((pill) => pill.style.removeProperty('display'));
      }

      setHiddenCount(hidden);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [hasMultipleAttachments, attachmentLayoutSignature, isExpanded]);

  if (!visibleAttachments.length) {
    return null;
  }

  return (
    <Box sx={{ pt: 0.75, pb: 0.75 }}>
      <Stack ref={gridRef} direction="row" sx={attachmentRowSx}>
        {visibleAttachments.map((attachment) => {
          const isWide = isSupportedAudioAttachmentContentType(attachment.contentType);

          return (
            <Box
              key={attachment.id}
              data-compact-attachment={!isWide}
              data-testid="message-attachment-item"
              sx={getAttachmentItemSx(isWide)}
            >
              <AttachmentMessageContent attachment={attachment as never} compact={!isWide} />
            </Box>
          );
        })}

        {hasMultipleAttachments ? (
          <Button
            ref={toggleRef}
            size="small"
            variant="text"
            sx={attachmentMoreButtonSx}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Show less' : `+${hiddenCount} more`}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};
